import React, {useRef, useState} from 'react'
import styled, {StyleSheetManager} from 'styled-components'
import rtlPlugin from 'stylis-plugin-rtl'
import Head from 'next/head'
import Script from 'next/script'
import {GetStaticPaths, GetStaticProps, GetStaticPathsResult} from 'next'
import {useRouter} from 'next/router'
import type {Entry} from 'contentful'

import Header, {IHeader} from '@miro-site/components_header'
import Footer, {IFooter} from '@miro-site/components_footer'
import {useLocalization} from '@miro-site/localization'
import PreviewMode from '@miro-site/core/src/components/preview-mode/PreviewMode'
import {theme as acqusitionHeaderTheme} from '@miro-site/components_acquisition-header'

import Components from '@miro-site/lp-builder-pages/src/utils/components'
import SectionRenderer from '@miro-site/lp-builder-pages/src/components/SectionRenderer'
import PreviewExperiments from '@miro-site/lp-builder-pages/src/components/PreviewExperiments'

import logger from '../src/logger/logger'
import getMenu from '../src/lib/contentful/getMenu'
import getPageData from '../src/lib/contentful/getPageData'
import getTextTokens from '../src/lib/contentful/getTextTokens'
import getPageBySlug from '../src/lib/contentful/getPageBySlug'
import {parseSections, toAbsoluteUrl} from '../src/lib/contentful/helpers'
import {LandingPage, Translation} from '../src/lib/contentful/types'

export const getStaticPaths: GetStaticPaths<{slug: string[]}> = async context => {
  const staticPaths: GetStaticPathsResult<{slug: string[]}> = {
    paths: [],
    fallback: 'blocking',
  }

  // We are using fallback behaviour for our dev mode to make performance better
  if (process.env.IS_DEVELOPMENT_SERVER) {
    return staticPaths
  }

  const pageData = await getPageData()
  const paths = Object.keys(pageData)

  for (const path of paths) {
    const pathSplit = path.split('/')
    const firstParam = pathSplit[0]
    const localized = !!firstParam && context?.locales?.includes(firstParam)

    let locale = context.defaultLocale
    let slug = pathSplit

    if (localized) {
      locale = firstParam
      slug = pathSplit.slice(1)
    }

    const experimentsData = pageData[path].entry.fields.experimentsData

    experimentsData?.combinations.forEach(comb => {
      staticPaths.paths.push({
        params: {
          slug: [...slug, comb],
        },
        locale,
      })
    })

    staticPaths.paths.push({
      params: {
        slug,
      },
      locale,
    })
  }

  return staticPaths
}

export type LandingPageProps = {
  header: IHeader | null
  footer: IFooter | null
  page: Entry<LandingPage>
  translations: Translation[]
  textTokens: Record<string, string>
  preview?: boolean
  previewExperiments: Record<string, string[]>
  experiments: Record<string, string>
}

export const getStaticProps: GetStaticProps<LandingPageProps> = async context => {
  let slug =
    (Array.isArray(context?.params?.slug)
      ? context?.params?.slug.join('/')
      : context?.params?.slug) || ''

  const preview = !!context.preview

  const log = logger.child({slug: slug, preview})

  const revalidate = process.env.NEXT_REVALIDATE_TIMEOUT

  log.info('Starting regeneration...')

  let experiments = {}

  try {
    // Tries to parse last part of slug as a base64 string
    const slugParts = slug.split('/')
    experiments = JSON.parse(Buffer.from(slugParts[slugParts.length - 1], 'base64').toString())

    slug = slugParts.slice(0, -1).join('/')
  } catch (e) {
    // ignore
  }

  const textTokens = await getTextTokens()

  const pagePath = context.locale === context.defaultLocale ? slug : `${context.locale}/${slug}`
  const pageData = await getPageBySlug(pagePath, preview)

  const {entry: page} = pageData || {}

  if (!page) {
    log.info('Page not found')

    return {
      notFound: true,
      revalidate: revalidate ? Number(revalidate) : false,
    }
  }

  log.info(`Found page with locale: ${page.sys.locale}`)

  let header: LandingPageProps['header'] = null
  let footer: LandingPageProps['footer'] = null

  if (!page.fields.acquisitionPage) {
    log.info('Fetching menu data...')

    const menu = await getMenu(page.sys.locale, preview)

    header = menu[0] || null
    footer = menu[1] || null
  }

  log.info('Regeneration finished')

  let {translations = []} = pageData || {}

  if (slug.indexOf('homepage') !== -1) {
    const homepageLocales = ['en', 'fr', 'es', 'pt', 'nl', 'de', 'ja', 'it', 'ru', 'ko']

    // Page is part of homepage experiment and we should:
    // 1 - Show all translations
    // 2 - Add static hreflang
    translations = []

    homepageLocales.forEach(locale => {
      translations.push({
        locale,
        isDefault: locale === 'en',
        url: locale === 'en' ? '' : locale,
      })
    })
  }

  return {
    props: {
      header,
      footer,
      page,
      translations,
      preview,
      experiments,
      previewExperiments: page.fields.experimentsData?.experiments ?? {},
      textTokens: textTokens[page.sys.locale],
    },
    revalidate: revalidate ? Number(revalidate) : false,
  }
}

const Container = styled.div`
  position: relative;
  min-height: 100%;
  height: auto !important;
`

const HeaderWrapper = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 200;

  &.mobile-menu-opened {
    position: fixed;
    z-index: 700;
  }
`

const PreviewComponentsWrapper = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 101;
  background-color: ${props => props.theme.colors.primaryIndigo};
  color: ${props => props.theme.colors.primaryWhite};
`

const Page = (props: LandingPageProps): React.ReactElement => {
  const {header, footer, page, translations, preview, textTokens, experiments, previewExperiments} =
    props

  const router = useRouter()
  const headerWrapperRef = useRef<HTMLDivElement>(null)
  const {isRTL} = useLocalization()
  const [selectedTreatments, setSelectedTreatments] = useState(experiments)

  if (router.isFallback) {
    return <></>
  }

  const sectionsToRender = parseSections(page.fields.layout ?? [], textTokens, selectedTreatments)

  const getHeaderComponent = () => {
    if (header) {
      return (
        <HeaderWrapper ref={headerWrapperRef}>
          <Header containerNode={headerWrapperRef.current} {...header} />
        </HeaderWrapper>
      )
    }

    // Guess the background for Acquisition Header component based on the first component we will render
    let headerBackground

    if (
      page.fields.acquisitionPage &&
      sectionsToRender[0] &&
      typeof sectionsToRender[0].values.background === 'string' &&
      acqusitionHeaderTheme.background[sectionsToRender[0].values.background]
    ) {
      headerBackground = sectionsToRender[0].values.background
    }

    return <Components.AcquisitionHeader background={headerBackground} />
  }

  const getFooterComponent = () => {
    if (footer) {
      return <Footer {...footer} />
    }

    return <Components.AcquisitionFooter />
  }

  const defaultOpenGraphImage = `${
    process.env.NEXT_PUBLIC_ASSET_PREFIX || ''
  }/assets/images/miro.png`

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <StyleSheetManager stylisPlugins={isRTL && [rtlPlugin]}>
      <>
        <Head>
          <title>{page.fields.title}</title>
          <meta name="description" content={page.fields.description} />

          <link rel="canonical" href={page.fields.canonicalUrl} />

          {translations.map((l, i) => {
            const hreflang = l.isDefault ? 'x-default' : l.locale

            return <link key={i} rel="alternate" href={toAbsoluteUrl(l.url)} hrefLang={hreflang} />
          })}

          <meta property="og:site_name" content="https://miro.com/" />
          <meta property="og:type" content="website" />
          <meta property="og:determiner" content="a" />

          <meta property="og:title" content={page.fields.openGraphTitle || page.fields.title} />
          <meta
            property="og:description"
            content={page.fields.openGraphDescription || page.fields.description}
          />
          <meta
            property="og:image"
            content={
              page.fields.openGraphImage?.fields?.file
                ? 'https:' + page.fields.openGraphImage.fields.file.url
                : defaultOpenGraphImage
            }
          />

          {page.fields.noIndex && <meta name="robots" content="noindex" />}

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content="https://miro.com/" />
          <meta name="twitter:title" content={page.fields.openGraphTitle || page.fields.title} />
          <meta
            name="twitter:description"
            content={page.fields.openGraphDescription || page.fields.description}
          />
          <meta
            name="twitter:image"
            content={
              page.fields.openGraphImage?.fields?.file
                ? 'https:' + page.fields.openGraphImage.fields.file.url
                : defaultOpenGraphImage
            }
          />
        </Head>

        {getHeaderComponent()}
        <Container>
          <main>
            {sectionsToRender.map((section, i) => (
              <SectionRenderer key={i} section={section} />
            ))}
          </main>
        </Container>
        {getFooterComponent()}
        {page.fields.customScripts &&
          page.fields.customScripts.reduce<JSX.Element[]>((acc, item) => {
            if (item.fields?.file?.url) {
              acc.push(
                <Script
                  key={item.fields.file.url}
                  src={item.fields.file.url}
                  strategy="lazyOnload"
                />,
              )
            }

            return acc
          }, [])}

        {preview && (
          <PreviewComponentsWrapper>
            <PreviewExperiments
              selectedTreatments={selectedTreatments}
              setSelectedTreatments={setSelectedTreatments}
              previewExperiments={previewExperiments}
            />

            <PreviewMode
              stopPreviewPath={`${
                process.env.NEXT_PUBLIC_API_BASE_PATH
                  ? process.env.NEXT_PUBLIC_API_BASE_PATH
                  : '/api'
              }/stop-preview/`}
            />
          </PreviewComponentsWrapper>
        )}
      </>
    </StyleSheetManager>
  )
}

export default Page
